import React, { useContext, useMemo, useRef, useState } from 'react';
import CardBox from '../../../CardBox';
import styles from './index.module.scss';
import { ShareDataContext } from '@/pages/IndustrialGreenMicBigScreen';
import { useRequest, useSize } from 'ahooks';
import { apiV2MicrogridOverviewGetNewEnergyGenerationTrendPost } from '@maxtropy/intelligent-dispath-apis-v2';
import { Spin } from 'antd';
import { formatElectricity, keepPercentTwoLine, keepTwoOrLine } from '@/pages/IndustrialGreenMicBigScreen/utils';
import CustomTooltip from '../../../CustomTooltip';
import icon_yesterday from '../../../../imgs/icon-yesterday.png';
import icon_today from '../../../../imgs/icon-today.png';
import ReactEcharts from 'echarts-for-react';
import { getChartOption } from './chart';
import { Empty } from '@maxtropy/components';
import { isNil } from 'lodash-es';

export enum ModeType {
  PV = 1,
  WP = 2,
}

const NewEnergyGenerationTrend = () => {
  const { microgridId, dataExit } = useContext(ShareDataContext);
  const [mode, setMode] = useState<ModeType>(ModeType.PV);
  const leftRef = useRef<HTMLElement>();
  const leftSize = useSize(leftRef);

  // 新能源发电趋势
  const { data, loading } = useRequest(
    () => {
      return apiV2MicrogridOverviewGetNewEnergyGenerationTrendPost({
        id: microgridId,
      });
    },
    {
      ready: !!microgridId,
      refreshDeps: [microgridId],
      pollingInterval: 1000 * 60 * 15,
    }
  );

  const pvDetailData = useMemo(() => {
    if (mode === ModeType.PV) {
      return data?.pvTrend;
    }
    return undefined;
  }, [data, mode]);

  const wpDetailData = useMemo(() => {
    if (mode === ModeType.WP) {
      return data?.wpTrend;
    }
  }, [data, mode]);

  // 查询图表
  const chartOption = useMemo(() => getChartOption(mode, data), [data, mode]);

  // 判断是否都为空，如果都为空则显示empty
  const isShowEmpty = useMemo(() => {
    if (mode === ModeType.WP) {
      if (data?.wpTrend?.actData?.length || data?.wpTrend?.predictData?.length) {
        return false;
      }
    }
    if (mode === ModeType.PV) {
      if (data?.pvTrend?.trendData?.actData?.length || data?.pvTrend?.trendData?.predictData?.length) {
        return false;
      }
    }
    return true;
  }, [data, mode]);

  // 判断温度湿度光照强度是否全为空
  const isShowTopEmpty = useMemo(() => {
    return !isNil(pvDetailData?.humidity) || !isNil(pvDetailData?.illuminance) || !isNil(pvDetailData?.temperature);
  }, [pvDetailData]);

  return (
    <CardBox
      title="新能源发电趋势"
      extraContent={
        <div className={styles.btnList}>
          <div onClick={() => setMode(ModeType.PV)} className={mode === ModeType.PV ? styles.btnActive : styles.btn}>
            光伏
          </div>
          {dataExit?.wpExists && (
            <div onClick={() => setMode(ModeType.WP)} className={mode === ModeType.WP ? styles.btnActive : styles.btn}>
              风电
            </div>
          )}
        </div>
      }
    >
      {loading ? (
        <Spin spinning={true} />
      ) : (
        <>
          {mode === ModeType.PV && isShowTopEmpty && (
            <div className={styles.top_box}>
              {!isNil(pvDetailData?.humidity) && (
                <div className={styles.item}>
                  <div className={styles.label}>湿度：</div>
                  <CustomTooltip
                    title={`${keepPercentTwoLine(pvDetailData?.humidity)}
                %`}
                  >
                    <div className={styles.value}>
                      {keepPercentTwoLine(pvDetailData?.humidity)}
                      <span className={styles.unit}>%</span>
                    </div>
                  </CustomTooltip>
                </div>
              )}
              {!isNil(pvDetailData?.temperature) && (
                <div className={styles.item}>
                  <div className={styles.label}>温度：</div>
                  <CustomTooltip
                    title={`${keepTwoOrLine(pvDetailData?.temperature)}
                ℃`}
                  >
                    <div className={styles.value}>
                      {keepTwoOrLine(pvDetailData?.temperature)}
                      <span className={styles.unit}>℃</span>
                    </div>
                  </CustomTooltip>
                </div>
              )}
              {!isNil(pvDetailData?.illuminance) && (
                <div className={styles.item_light}>
                  <div className={styles.label}>光照强度：</div>
                  <CustomTooltip
                    title={`${keepTwoOrLine(pvDetailData?.illuminance)}
                lx`}
                  >
                    <div className={styles.value}>
                      {keepTwoOrLine(pvDetailData?.illuminance)}
                      <span className={styles.unit}>lx</span>
                    </div>
                  </CustomTooltip>
                </div>
              )}
            </div>
          )}

          <div className={styles.center_box}>
            <div
              className={`${styles.item} ${styles.left}`}
              ref={ref => {
                if (ref) {
                  leftRef.current = ref;
                }
              }}
            >
              <img className={styles.img_sty} src={icon_yesterday} alt="" />
              <div className={styles.right_box}>
                <div className={styles.label}>昨日实发/预测</div>
                <CustomTooltip
                  title={
                    mode === ModeType.PV
                      ? `${formatElectricity(pvDetailData?.trendData?.lastValue)}/${formatElectricity(
                          pvDetailData?.trendData?.lastPredictValue
                        )}`
                      : `${formatElectricity(wpDetailData?.lastValue)}/${formatElectricity(
                          wpDetailData?.lastPredictValue
                        )}`
                  }
                >
                  <div className={styles.value}>
                    {mode === ModeType.PV
                      ? `${formatElectricity(pvDetailData?.trendData?.lastValue)}/${formatElectricity(
                          pvDetailData?.trendData?.lastPredictValue
                        )}`
                      : `${formatElectricity(wpDetailData?.lastValue)}/${formatElectricity(
                          wpDetailData?.lastPredictValue
                        )}`}
                  </div>
                </CustomTooltip>
              </div>
            </div>
            <div className={`${styles.item}`} style={{ width: `calc(100% - ${leftSize?.width}px)` }}>
              <img className={styles.img_sty} src={icon_today} alt="" />
              <div className={styles.right_box}>
                <div className={styles.label}>今日预测</div>
                <CustomTooltip
                  title={
                    mode === ModeType.PV
                      ? `${formatElectricity(pvDetailData?.trendData?.todayPredict)}`
                      : `${formatElectricity(wpDetailData?.todayPredict)}`
                  }
                >
                  <div className={styles.value}>
                    {mode === ModeType.PV
                      ? `${formatElectricity(pvDetailData?.trendData?.todayPredict)}`
                      : `${formatElectricity(wpDetailData?.todayPredict)}`}
                  </div>
                </CustomTooltip>
              </div>
            </div>
          </div>
          {isShowEmpty ? (
            <Empty className={styles.empty_box} />
          ) : (
            <div className={styles.chartWrapper}>
              <ReactEcharts
                style={{ width: '100%', height: '100%' }}
                option={chartOption}
                notMerge
                lazyUpdate={false}
              />
            </div>
          )}
        </>
      )}
    </CardBox>
  );
};

export default NewEnergyGenerationTrend;
